// The lists' registrations (lane B10 S19; loaded by views/b10/register.ts; lane contract C-B10-13). B10's pieces of the
// two lists the owning lanes left slots in, each registered once from here:
//   lane B2's Requisitions list (views/requisition/slots.ts): the one batched `requisition.signals` read, the Req
//     health · Search Readiness column (deny by default: only a reader who holds the tool) and the saved views of
//     surface `requisitions`, applied through the list's own address (the table/board choice kept);
//   lane B3's Candidates table (views/candidate/slots.ts): the saved views of surface `candidates`, its status and
//     internal/external filters and never its search (Law 9), so applying a view keeps the search as typed.
// The lists, their routes and their gates stay the owning lanes'; a second registration of a key throws.

import { createElement } from "react";
import { registerCandidateSavedViews } from "@/views/candidate/slots";
import { registerListSignals, registerRequisitionColumn, registerSavedViews, type SavedViewsProps } from "@/views/requisition/slots";
import { LIST_VIEW_FIELDS, SavedViewsSelect, queryFromSpec, specFromQuery, type ViewSpec } from "@/views/b10/lib/savedViews";
import { RowSignals, SIGNALS_COLUMN, useSignalsQuery } from "./RowSignals";

const REQUISITION_FIELDS = LIST_VIEW_FIELDS.requisitions;
const CANDIDATE_FIELDS = LIST_VIEW_FIELDS.candidates;

/** The Requisitions list's flags for a saved view: its filters replaced, the page reset, the table/board choice kept. */
export const requisitionViewQuery = (spec: ViewSpec, query: URLSearchParams): string =>
  queryFromSpec(spec, REQUISITION_FIELDS, query, ["view"]).toString();

/** The Candidates table's flags for a saved view: its two filters replaced, the search kept as typed (never stored). */
export const candidateViewQuery = (spec: ViewSpec, query: URLSearchParams): string =>
  queryFromSpec(spec, CANDIDATE_FIELDS, query, ["q"]).toString();

export function RequisitionSavedViews({ query, apply }: SavedViewsProps) {
  return createElement(SavedViewsSelect, {
    surface: "requisitions",
    current: specFromQuery(query, REQUISITION_FIELDS),
    onApply: (spec) => apply(requisitionViewQuery(spec, query)),
  });
}

export function CandidateSavedViews({ query, apply }: SavedViewsProps) {
  return createElement(SavedViewsSelect, {
    surface: "candidates",
    current: specFromQuery(query, CANDIDATE_FIELDS),
    onApply: (spec) => apply(candidateViewQuery(spec, query)),
  });
}

registerListSignals({ key: "signals", lane: "B10", useSignals: useSignalsQuery });
registerRequisitionColumn({
  key: "signals",
  lane: "B10",
  header: SIGNALS_COLUMN,
  visible: (can) => can("requisition.signals"),
  cell: (_row, context) => createElement(RowSignals, { signal: context?.signal, loading: context?.loading, links: context?.links ?? true }),
});
registerSavedViews({ key: "saved-views", lane: "B10", component: RequisitionSavedViews });
registerCandidateSavedViews({ key: "saved-views", lane: "B10", component: CandidateSavedViews });
